"use client";

import { FC, PropsWithChildren, useCallback, useContext, useState } from "react";
import { SportFavoritesContext, LiveFeedContext } from "@finbackoffice/site-core";
import classNames from "classnames";
import { usePathname } from "next/navigation";
import { ISportFeedResponse } from "@finbackoffice/websocket-client";
import { AssetsDirectory } from "@finbackoffice/enums";
import { useDevice, useScrollToSelectedSport } from "hooks";
import { FeedScopeEnum } from "utils/constants";
import { RouteNames } from "utils/constants";
import { Svg } from "components/base/svg/Svg";
import Sport from "components/sport/base/sport/Sport";
import Popover from "components/base/popover/Popover";
import NoGames from "components/base/no-games/NoGames";
import { Carousel, CarouselSlide } from "components/base/carousel/Carousel";
import AdvancedSidebarPrematchSport from "components/side-bar/advanced/prematch/sport/AdvancedSidebarPrematchSport";
import AdvancedSidebarLiveSport from "components/side-bar/advanced/live/sport/AdvancedSidebarLiveSport";
import Translate from "components/base/translate/Translate";
import { NavigationBar } from "components/base/navigation/NavigationBar";
import styles from "./sports-layout.module.sass";

const Favorites: FC<{ feed: FeedScopeEnum }> = ({ feed }) => {
    const { liveTopLeaguesGamesCount } = useContext(LiveFeedContext);
    const { favLiveLeaguesGameCount, favPrematchLeaguesGameCount } =
        useContext(SportFavoritesContext);

    return (
        <span className={classNames(styles.favorites, styles.active)}>
            <Svg
                src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/favourites.svg`}
                className="svg-favorite-icon"
            />
            <Translate tid="sport_favorites_tab" />
            <span className={styles.gamesCount}>
                {feed === FeedScopeEnum.live && liveTopLeaguesGamesCount + favLiveLeaguesGameCount}
                {feed === FeedScopeEnum.prematch && favPrematchLeaguesGameCount}
            </span>
        </span>
    );
};

interface IProps {
    sports: ISportFeedResponse[];
    activeSportId?: number;
    withPopover?: boolean;
    onSportClick?: (sportId: number) => void;
    variant?: "default" | "widget";
    feed: FeedScopeEnum;
    showNoGamesView?: boolean;
    multiSelectedSportIds?: number[];
}

const SportsLayout: FC<PropsWithChildren<IProps>> = ({
    sports,
    children,
    activeSportId,
    onSportClick,
    withPopover = false,
    variant = "default",
    feed,
    multiSelectedSportIds,
    showNoGamesView = false,
}) => {
    const pathname = usePathname();
    const { isMobileDevice } = useDevice();
    const [popoverLastPosition, setPopoverLastPosition] = useState<{
        top: number | null;
        left: number | null;
    }>({
        top: null,
        left: null,
    });
    const { liveTopLeagues } = useContext(LiveFeedContext);
    const { favEvents, favPrematchLeagues } = useContext(SportFavoritesContext);

    const multiSelectActiveSportId =
        multiSelectedSportIds &&
        (multiSelectedSportIds.length < 2
            ? multiSelectedSportIds[0]
            : multiSelectedSportIds[multiSelectedSportIds.length - 1]);

    const showFavorites =
        pathname?.includes(`/${RouteNames.LIVE}/${RouteNames.TOP}`) ||
        pathname?.includes(`/${RouteNames.PREMATCH}/${RouteNames.FAVORITES}`);

    const activeSportIdForScroll = multiSelectActiveSportId ?? activeSportId ?? null;

    const { containerRef, selectedSportIndex } = useScrollToSelectedSport(
        activeSportIdForScroll,
        sports,
        showFavorites ? 1 : 0,
    );
    const hasFavorite = useCallback(
        (id: number) => {
            if (feed === FeedScopeEnum.live) {
                return favEvents.some((event) => event.sport_id === id);
            }

            if (feed === FeedScopeEnum.prematch) {
                return favPrematchLeagues.some((event) => event.sport_id === id);
            }

            return false;
        },
        [favEvents, favPrematchLeagues, feed],
    );

    const renderSport = useCallback(
        (sport: ISportFeedResponse, index: number) => (
            <Popover
                key={index}
                disabled={!withPopover}
                closeOnOrientationChange={true}
                onPositionChange={(position) => {
                    setPopoverLastPosition({
                        top: position.top,
                        left: position.left,
                    });
                }}
                onOpen={() => {
                    if (withPopover) {
                        onSportClick?.(sport.id);
                    }
                }}
                onClose={() => {
                    setPopoverLastPosition({ top: null, left: null });
                }}
                initialPosition={popoverLastPosition}
                scrollRef={containerRef}
                scrollBehavior="close"
                placement="bottom"
                contentClassName={styles.popoverContent}
                trigger={
                    <Sport
                        type={withPopover ? "button" : variant === "widget" ? "button" : "link"}
                        key={sport.id}
                        sport={sport}
                        active={!withPopover && !showFavorites && sport.id === activeSportId}
                        isSelected={
                            multiSelectedSportIds ? multiSelectedSportIds.includes(sport.id) : false
                        }
                        hasFavorite={
                            hasFavorite(sport.id) ||
                            liveTopLeagues.some((tournament) => tournament.sport_id === sport.id)
                        }
                        onClickHandler={!withPopover ? onSportClick : undefined}
                    />
                }>
                <div>
                    {feed === FeedScopeEnum.live && (
                        <AdvancedSidebarLiveSport sport={sport} openedByDefault disableCollapsing />
                    )}
                    {feed === FeedScopeEnum.prematch && (
                        <AdvancedSidebarPrematchSport
                            sport={sport}
                            openedByDefault
                            disableCollapsing
                        />
                    )}
                </div>
            </Popover>
        ),
        [
            activeSportId,
            containerRef,
            feed,
            hasFavorite,
            liveTopLeagues,
            multiSelectedSportIds,
            onSportClick,
            popoverLastPosition,
            showFavorites,
            variant,
            withPopover,
        ],
    );

    if (sports.length === 0 && showNoGamesView) {
        return (
            <>
                <NoGames wrapperClassName={styles.noGame} />
                {children}
            </>
        );
    }

    return (
        <>
            <NavigationBar ref={containerRef} dataTestId="sportsbook-sports-container">
                {showFavorites && <Favorites feed={feed} />}
                {!isMobileDevice && sports.length ? (
                    <Carousel
                        options={{ dragFree: true }}
                        initialScrollIndex={
                            selectedSportIndex >= 0 ? selectedSportIndex : undefined
                        }>
                        {sports.map((sport: ISportFeedResponse, index: number) => (
                            <CarouselSlide key={index}>{renderSport(sport, index)}</CarouselSlide>
                        ))}
                    </Carousel>
                ) : (
                    <>{sports.map(renderSport)}</>
                )}
            </NavigationBar>
            {children}
        </>
    );
};

export default SportsLayout;
